/**
 * CIS契約マスタ CSV アップロードの frontend 前段チェック
 * (spec `docs/specs/admin/data/uploads/cis-contract-master.md` §1 列フォーマット)。
 *
 * このモジュールは **行の値を解釈しない** (囲みの正しさだけ読んで検査する)。CSV は生バイトのまま
 * ImportCisContracts RPC に送り、厳格モード (ヘッダー完全一致・列数・値域・
 * 日付形式) と差分判定は backend が行う (cis_contract_service.proto の
 * ImportCisContractsRequest doc 参照)。
 *
 * ここで見るのは common.md Step1 の「クライアント側構文チェック」に対応する
 * 軽い前段だけ — ファイルサイズ / 文字コード / ダブルクォートの囲み (AC-DUC-045) /
 * ヘッダー行 / データ行の有無。
 * サーバー往復なしで即座に弾ける誤ファイルを弾くのが目的で、サーバー側検証を
 * 置き換えるものではない (common.md「クライアント側 + サーバー側の二重化」)。
 */
import { CIS_CONTRACT_CSV_ACCEPTED_HEADERS } from '~/models/cisContractCsvHeader'
import { isAcceptedCsvHeader } from '~/models/csvAcceptedHeaders'
import { hasCsvDataRow, readCsvRecords } from '~/models/csvRecords'

export type PrecheckResult =
  | { ok: true }
  | {
      ok: false
      messageKey: PrecheckErrorKey
      params?: Record<string, number | string>
    }

export type PrecheckErrorKey =
  | 'fileSizeExceeded'
  | 'readFailed'
  | 'notUtf8'
  | 'quoteInvalid'
  | 'headerMismatch'
  | 'noDataRows'

/** UTF-8 BOM。Excel が既定で付けるため許容する (common.md)。 */
const UTF8_BOM = '﻿'

/** エラー文へ載せる実際ヘッダーの表示上限。 */
const MAX_HEADER_ECHO = 200

function truncateHeader(s: string): string {
  const trimmed = s.trim()
  return trimmed.length <= MAX_HEADER_ECHO
    ? trimmed
    : `${trimmed.slice(0, MAX_HEADER_ECHO)}…`
}

/**
 * ヘッダーのセルをサーバーと同じ緩さに正規化してから比較する。
 *
 * backend の `validateHeader` は csv.Reader で unquote した上でセル単位に
 * TrimSpace する (Excel が引用符やセル前後の空白を付けるため)。前段がそれより
 * 厳しいと `"供給地点特定番号","エリア",…` のようにサーバーなら通る CSV を Step1 で
 * 門前払いしてしまう。common.md の二重化は「前段は緩く、サーバーが正」なので、
 * 前段の判定はサーバーと同等かそれより緩くする。囲みを外す処理は共通の
 * `readCsvRecords` が持つ。
 */
function trimHeaderCells(cells: string[]): string[] {
  return cells.map((cell) => cell.trim())
}

/**
 * Step1 の前段チェックを行う。行の値は見ない (backend の責務)。
 * ファイルサイズ上限は呼び出し側が読み込み**前**に判定する (本関数は復号済みの
 * text を受け取るため、ここで見ても手遅れになる)。
 *
 * 行数上限 (100,000 行) はここでは見ない。理由は 2 つ:
 *   1. common.md のクライアント側チェック対象は「列数 / ヘッダー行 / 文字コード /
 *      ファイルサイズ / ダブルクォートの囲み」で、行数は含まれない
 *   2. 引用符内の改行は CSV として有効で backend の csv.Reader は 1 レコードと
 *      数えるため、物理行数は論理レコード数以上にしかならない。つまり
 *      「物理行数 > 上限」から「論理レコード数 > 上限」は導けず、この数え方で
 *      拒否するとサーバーなら通る CSV を前段で弾いてしまう
 *      (common.md の二重化は「前段は緩く、サーバーが正」)。
 * 「データ行が 0 件」だけは物理行 0 → 論理レコード 0 が常に成り立つので前段で見る。
 */
export function precheckCisContractCsv(text: string): PrecheckResult {
  const body = text.startsWith(UTF8_BOM) ? text.slice(UTF8_BOM.length) : text
  const lines = body.split(/\r?\n/)
  const rawHeader = lines[0] ?? ''
  // 囲みが正しくない行は、ヘッダー行も含めて Step1 で拒否する (AC-DUC-045)。
  const read = readCsvRecords(body, ',')
  if (!read.ok) {
    return {
      ok: false,
      messageKey: 'quoteInvalid',
      params: { row: read.quoteErrorRow },
    }
  }
  const { records } = read
  const headerCells = trimHeaderCells(records[0]?.cells ?? [])
  // 7 列 (旧形式) / 8 列 (事業者コード付き) × 日本語 / 英語 の 4 パターンを受理する
  // (AC-DUI-001、personal-settings.md AC-SPS-031。英語は大文字・小文字を区別しない)。
  if (!isAcceptedCsvHeader(headerCells, CIS_CONTRACT_CSV_ACCEPTED_HEADERS)) {
    // AC-DUC-003: 期待ヘッダーだけでなく「実際のヘッダー」も見せる
    // (自分のファイルの 1 行目が何だったか画面で確認できるように)。
    return {
      ok: false,
      messageKey: 'headerMismatch',
      params: { actual: truncateHeader(rawHeader) },
    }
  }

  if (!hasCsvDataRow(records, 0)) {
    return { ok: false, messageKey: 'noDataRows' }
  }
  return { ok: true }
}
